import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

export interface Column<T> {
  key: string;
  label: ReactNode;
  render?: (row: T) => ReactNode;
  value?: (row: T) => string | number | null | undefined;   // for sorting and filtering
  numeric?: boolean;
  className?: string;
}

/** Sortable (click a header), filterable (the search box), and rows can link
 *  to a detail page. Sorting and filtering use `value`, falling back to the
 *  field named by `key`. */
export function DataTable<T>({
  rows, columns, rowKey, href, filterPlaceholder = "Filter…", empty = "Nothing to show.",
  initialSort, toolbar, footer, testId, limit,
}: {
  rows: T[]; columns: Column<T>[]; rowKey: (row: T) => string | number; href?: (row: T) => string | null;
  filterPlaceholder?: string | false; empty?: ReactNode; initialSort?: { key: string; dir: "asc" | "desc" };
  toolbar?: ReactNode; footer?: ReactNode; testId?: string;
  /** Show this many rows (after filter and sort) until "Show all" is pressed. */
  limit?: number;
}) {
  const [q, setQ] = useState("");
  const [all, setAll] = useState(false);
  const [sort, setSort] = useState(initialSort ?? null);
  const nav = useNavigate();

  const val = (row: T, col: Column<T>) =>
    col.value ? col.value(row) : (row as Record<string, unknown>)[col.key] as string | number | null | undefined;

  const shown = useMemo(() => {
    let out = rows;
    const needle = q.trim().toLowerCase();
    if (needle) {
      out = out.filter((r) => columns.some((c) => String(val(r, c) ?? "").toLowerCase().includes(needle)));
    }
    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      if (col) {
        out = [...out].sort((a, b) => {
          const x = val(a, col), y = val(b, col);
          if (x == null && y == null) return 0;
          if (x == null) return 1;
          if (y == null) return -1;
          const cmp = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true });
          return sort.dir === "asc" ? cmp : -cmp;
        });
      }
    }
    return out;
  }, [rows, q, sort, columns]);

  const toggle = (key: string) =>
    setSort((s) => (s?.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  return (
    <div className="table-wrap" data-testid={testId}>
      {(filterPlaceholder !== false || toolbar) && (
        <div className="table-tools">
          {filterPlaceholder !== false && (
            <input type="search" className="filter" placeholder={filterPlaceholder}
                   aria-label="Filter rows" value={q} onChange={(e) => setQ(e.target.value)} />
          )}
          {toolbar}
          <span className="muted count" aria-live="polite">{shown.length} of {rows.length}</span>
        </div>
      )}
      <div className="table-scroll">
        <table className="grid">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={`${c.numeric ? "n" : ""} ${c.className ?? ""}`}
                    aria-sort={sort?.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
                  <button className="th-sort" onClick={() => toggle(c.key)}>
                    {c.label}
                    <span className="sort-ind" aria-hidden="true">{sort?.key === c.key ? (sort.dir === "asc" ? "▲" : "▼") : ""}</span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={columns.length} className="empty-cell">{q ? `No rows match "${q}".` : empty}</td></tr>
            )}
            {(limit && !all ? shown.slice(0, limit) : shown).map((r) => {
              const link = href?.(r);
              return (
                <tr key={rowKey(r)} className={link ? "is-link" : ""}
                    onClick={link ? (e) => { if (!(e.target as HTMLElement).closest("a,button")) nav(link); } : undefined}>
                  {columns.map((c) => (
                    <td key={c.key} className={`${c.numeric ? "n" : ""} ${c.className ?? ""}`}>
                      {c.render ? c.render(r) : String(val(r, c) ?? "—")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
          {footer && <tfoot>{footer}</tfoot>}
        </table>
      </div>
      {limit && !all && shown.length > limit && (
        <button className="link-btn table-more" onClick={() => setAll(true)}>
          Show all {shown.length} (the first {limit} are shown)
        </button>
      )}
    </div>
  );
}
